import { AsyncButton, ControlsGroup, ControlsGroupInputProps } from "aic";
import { IRequestStatus } from "functions/api/interface";
import { useState } from "react";
import { Container, Form, Modal, ModalProps } from "react-bootstrap";

export interface WordModalProps extends ModalProps {
  id: string;
  buttonLabel?: string;
  clearOnClose?: boolean;
  externalValues?: Record<string, string>;
  inputs: ControlsGroupInputProps[];
  message?: React.ReactElement | null;
  onCancel: () => void;
  onFieldChange?: (allValues: Record<string, string>) => void;
  onOk: (ret: Record<string, string>) => void;
  requestStatus?: IRequestStatus<unknown> | IRequestStatus<unknown>[];
  show: boolean;
  spellCheck?: boolean;
  title: string;
}

/**
 * Modal for entering one or more text fields with validation.
 *
 * @param props - Props for the WordModal component
 * @param props.id - ID for the modal
 * @param props.buttonLabel - Label for the OK button
 * @param props.clearOnClose - Whether to clear input values on close
 * @param props.externalValues - External values to merge with the input values
 * @param props.inputs - Array of input definitions
 * @param props.inputs[].computeValue - Function to compute the value based on other inputs
 * @param props.inputs[].dropdownOptions - Options for dropdown selection
 * @param props.inputs[].forcePrefix - Prefix to force on the input
 * @param props.inputs[].hidden - Whether the input is hidden or a function to determine it
 * @param props.inputs[].initialValue - Initial value of the input
 * @param props.inputs[].inputLabel - Label of the input
 * @param props.inputs[].inputName - Name of the input
 * @param props.inputs[].placeholder - Placeholder text for the input
 * @param props.inputs[].readonly - Whether the input is readonly or a function to determine it
 * @param props.inputs[].validConditions - Array of validation functions for the input
 * @param props.inputs[].validationRequestUrl - URL used for validation requests
 * @param props.message - Additional element to show above the inputs
 * @param props.onCancel - Callback when cancelling
 * @param props.onFieldChange - Callback when a field changes
 * @param props.onOk - Callback when OK is pressed, contains all values
 * @param props.requestStatus - Request status or array of request statuses for async operations
 * @param props.show - Whether to show the modal
 * @param props.spellCheck - Whether to enable spell checking on text inputs
 * @param props.title - Title of the modal
 */
export const WordModal = ({
  id,
  buttonLabel = "OK",
  clearOnClose = true,
  externalValues,
  inputs,
  message,
  onCancel,
  onFieldChange,
  onOk,
  requestStatus,
  show,
  spellCheck = true,
  title,
  ...rest
}: WordModalProps): React.ReactElement => {
  const [currentValues, setCurrentValues] = useState<Record<string, string>>(
    Object.fromEntries(
      inputs
        .filter((inp) => !inp.computeValue)
        .map((inp) => [inp.inputName, inp.initialValue ?? ""]),
    ),
  );

  const [textValid, setTextValid] = useState<boolean[]>(
    inputs.map((inp) => {
      const isHidden = typeof inp.hidden === "function" ? inp.hidden(currentValues) : false;
      return isHidden || !inp.validCondition;
    }),
  );

  /**
   * Clears the input fields if clearOnClose is true
   */
  const clearFields = () =>
    clearOnClose &&
    setCurrentValues(
      Object.fromEntries(
        inputs
          .filter((inp) => !inp.computeValue)
          .map((inp) => [
            inp.inputName,
            inp.initialValue ??
              // istanbul ignore next
              "",
          ]),
      ),
    );

  /**
   * Cancel button click handler
   */
  const onCancelClick = () => {
    clearFields();
    onCancel();
  };

  return (
    <Modal
      {...rest}
      id={id}
      show={show}
      onHide={() => {
        onCancelClick();
        rest.onHide?.();
      }}
      onExited={(n) => {
        rest.onExited?.(n);
        clearFields();
      }}
    >
      <Form id={`${id}-form`}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Container>{title}</Container>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            {message}
            <ControlsGroup
              baseId={`${id}-form`}
              currentValues={{ ...currentValues, ...externalValues }}
              inputs={inputs}
              onFieldChange={onFieldChange}
              setCurrentValues={(vals) => setCurrentValues(vals)}
              setTextValid={setTextValid}
              spellCheck={spellCheck}
            />
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <AsyncButton
            id={`${id}-ok`}
            variant="primary"
            disabled={textValid.some((value, ix) => {
              const isHidden =
                typeof inputs[ix].hidden === "function" ? inputs[ix].hidden(currentValues) : false;
              return !isHidden && !value;
            })}
            onClick={() => onOk(currentValues)}
            requestStatus={requestStatus}
          >
            {buttonLabel}
          </AsyncButton>
          <AsyncButton
            id={`${id}-cancel`}
            variant="secondary"
            onClick={onCancelClick}
          >
            Cancel
          </AsyncButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

WordModal.displayName = "WordModal";
